import { Money, PassProduct, PurchasedPass, SelectedSession, TimeInterval } from '../../types/booking/booking'
import { DayOfWeek } from '../dateUtils'

/**
 * Client-side mirror of the server's funding rules (PassEligibilityService / PassFundingPlanner), so the booking page
 * prices a selection instantly. It is advisory: the server quotes the checkout again when the booking is confirmed and
 * re-validates everything when the hold is created.
 */

const END_OF_DAY = 24 * 60
const WEEKDAYS: DayOfWeek[] = [
  DayOfWeek.SUNDAY,
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
]
const DAY_MS = 24 * 60 * 60 * 1000

type Range = { start: number; end: number }

/** Eligibility terms shared by owned passes and pass products. */
type PassTerms = {
  clubId?: number | null
  courtIds?: number[]
  weekdays?: DayOfWeek[]
  intervals?: TimeInterval[]
}

/** A session to fund: what the booking grid knows about a start. */
export type FundingSession = Pick<
  SelectedSession,
  'courtId' | 'startTime' | 'endTime' | 'durationMinutes' | 'price'
> & { clubId?: number }

export type SessionFunding = {
  session: FundingSession
  prepaid: boolean
  /** Minutes taken from owned passes. */
  ownedMinutes: number
  /** Minutes of a prepaid session that the top-up pass covers. */
  shortfall: number
}

export type FundingPlan = {
  items: SessionFunding[]
  total?: Money
  prepaidMinutes: number
  missingMinutes: number
  topUpApplied: boolean
  topUpOptions: PassProduct[]
  /** Minutes left on each owned pass once the selection is funded. */
  remaining: Map<number, number>
}

const minuteOf = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

const endMinuteOf = (time: string) => {
  const minute = minuteOf(time)
  return minute === 0 ? END_OF_DAY : minute
}

const merge = (intervals: TimeInterval[]): Range[] => {
  const merged: Range[] = []
  intervals
    .map(interval => ({ start: minuteOf(interval.startTime), end: endMinuteOf(interval.endTime) }))
    .sort((a, b) => a.start - b.start)
    .forEach(range => {
      const last = merged[merged.length - 1]
      if (last && range.start <= last.end) {
        last.end = Math.max(last.end, range.end)
      } else {
        merged.push({ ...range })
      }
    })
  return merged
}

const weekdayOf = (localDateTime: string) =>
  WEEKDAYS[new Date(`${localDateTime.slice(0, 10)}T00:00:00`).getDay()]

/** Local date-time (club wall clock) as an instant in ms; the browser zone stands in for the club's. */
const instantOf = (localDateTime: string) => new Date(localDateTime).getTime()

export const termsAllow = (terms: PassTerms, session: FundingSession) => {
  const courtAllowed = (terms.courtIds ?? []).includes(session.courtId)
  if (!courtAllowed || !(terms.weekdays ?? []).includes(weekdayOf(session.startTime))) {
    return false
  }
  const from = minuteOf(session.startTime.slice(11, 16))
  const to = from + session.durationMinutes
  return to <= END_OF_DAY && merge(terms.intervals ?? []).some(range => range.start <= from && to <= range.end)
}

const canFund = (pass: PurchasedPass, session: FundingSession) =>
  Boolean(pass.expiresAt) &&
  new Date(pass.expiresAt!).getTime() >= instantOf(session.endTime) &&
  termsAllow(pass, session)

const topUpCovers = (product: PassProduct, session: FundingSession, now: number) =>
  termsAllow(product, session) && now + product.validityDays * DAY_MS >= instantOf(session.endTime)

const byStart = (a: FundingSession, b: FundingSession) =>
  a.startTime === b.startTime ? a.courtId - b.courtId : a.startTime.localeCompare(b.startTime)

/**
 * Funds the sessions in start order: eligible owned minutes first (FEFO across passes), then the chosen top-up pass,
 * otherwise the single-session price. Sessions only partly covered by owned passes report missing minutes, for which
 * pass products that cover them are offered.
 */
export const planFunding = (
  sessions: FundingSession[],
  passes: PurchasedPass[],
  products: PassProduct[],
  topUpPassProductId?: number,
  now = Date.now()
): FundingPlan => {
  const usable = passes
    .filter(pass => pass.status === 'ACTIVE' && pass.expiresAt && new Date(pass.expiresAt).getTime() > now)
    .sort((a, b) => a.expiresAt!.localeCompare(b.expiresAt!) || a.id - b.id)
  const remaining = new Map(usable.map(pass => [pass.id, pass.availableMinutes]))
  const topUp = products.find(product => product.id === topUpPassProductId)
  const sorted = [...sessions].sort(byStart)

  let missingWithoutTopUp = 0
  sorted.forEach(session => {
    const eligible = usable
      .filter(pass => (remaining.get(pass.id) ?? 0) > 0 && canFund(pass, session))
      .reduce((sum, pass) => sum + (remaining.get(pass.id) ?? 0), 0)
    if (eligible > 0 && eligible < session.durationMinutes) {
      missingWithoutTopUp += session.durationMinutes - eligible
    }
  })

  let missing = 0
  const items: SessionFunding[] = sorted.map(session => {
    const eligible = usable.filter(
      pass => (remaining.get(pass.id) ?? 0) > 0 && canFund(pass, session)
    )
    const eligibleMinutes = eligible.reduce((sum, pass) => sum + (remaining.get(pass.id) ?? 0), 0)
    if (eligibleMinutes < session.durationMinutes && !(topUp && topUpCovers(topUp, session, now))) {
      return { session, prepaid: false, ownedMinutes: 0, shortfall: 0 }
    }
    let needed = session.durationMinutes
    for (const pass of eligible) {
      const take = Math.min(needed, remaining.get(pass.id) ?? 0)
      remaining.set(pass.id, (remaining.get(pass.id) ?? 0) - take)
      needed -= take
      if (needed === 0) {
        break
      }
    }
    missing += needed
    return { session, prepaid: true, ownedMinutes: session.durationMinutes - needed, shortfall: needed }
  })

  const topUpApplied = Boolean(topUp) && missing > 0
  const missingMinutes = topUp ? missing : missingWithoutTopUp
  const unpriced = items.some(item => !item.prepaid && !item.session.price)
  const currency = sessions.find(session => session.price)?.price?.currency ?? topUp?.currency ?? 'ron'
  const amount =
    items.reduce((sum, item) => sum + (item.prepaid ? 0 : (item.session.price?.amount ?? 0)), 0) +
    (topUpApplied && topUp ? topUp.price : 0)

  const topUpOptions =
    missingMinutes > 0
      ? products.filter(
          product =>
            product.active !== false &&
            !product.suspended &&
            product.includedMinutes >= missingMinutes &&
            sorted.every(session => topUpCovers(product, session, now))
        )
      : []

  return {
    items,
    total: sessions.length === 0 || unpriced ? undefined : { amount, currency },
    prepaidMinutes: items.filter(item => item.prepaid).reduce((sum, item) => sum + item.session.durationMinutes, 0),
    missingMinutes,
    topUpApplied,
    topUpOptions,
    remaining,
  }
}

/**
 * Whether one more session would be covered by the minutes left after funding the current selection (shown as
 * "Included" in the grid).
 */
export const wouldBeIncluded = (
  session: FundingSession,
  passes: PurchasedPass[],
  remaining: Map<number, number>
) =>
  passes
    .filter(pass => (remaining.get(pass.id) ?? 0) > 0 && canFund(pass, session))
    .reduce((sum, pass) => sum + (remaining.get(pass.id) ?? 0), 0) >= session.durationMinutes
